import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import { LEGAL_DOCS, LEGAL_ORDER, LEGAL_UPDATED } from "../lib/legal";

export default function Legal() {
  const { doc } = useParams();
  const data = LEGAL_DOCS[doc];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  if (!data) {
    return (
      <div className="bg-ink text-white min-h-screen flex items-center justify-center px-4" data-testid="legal-not-found">
        <div className="text-center">
          <h1 className="font-heading font-black uppercase text-2xl">Document not found</h1>
          <Link to="/" className="mt-4 inline-block text-cyan hover:underline">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ink text-white min-h-screen" data-testid={`legal-page-${doc}`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-cyan transition-colors">
          <ArrowLeft size={14} /> Back to Thortek Performance
        </Link>

        <h1 className="mt-8 font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl" data-testid="legal-title">
          {data.title}
        </h1>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
          Last updated: {LEGAL_UPDATED}
        </p>

        {doc === "disclaimer" && (
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-amber-200" data-testid="legal-safety-banner">
            <ShieldAlert size={20} className="shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed">
              Alignment affects steering, braking and handling. If you are not confident, stop and consult a qualified professional.
            </p>
          </div>
        )}

        <p className="mt-6 text-sm text-slate-300 leading-relaxed">{data.intro}</p>

        <div className="mt-8 space-y-7">
          {data.sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${i}`}>
              <h2 className="font-heading font-bold text-base text-white">{s.h}</h2>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
          {LEGAL_ORDER.map((slug) => (
            <Link
              key={slug}
              to={`/legal/${slug}`}
              data-testid={`legal-nav-${slug}`}
              className={`transition-colors hover:text-cyan ${slug === doc ? "text-cyan" : ""}`}
            >
              {LEGAL_DOCS[slug].label}
            </Link>
          ))}
        </div>

        <p className="mt-6 text-[11px] text-slate-600 leading-relaxed">
          These documents are provided for general information and are not legal advice.
        </p>
      </div>
    </div>
  );
}
